import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { Anchor, Button, Loader, Select, Stack, Text } from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { getData } from 'country-list'
import { usePostHog } from 'posthog-js/react'

import { useGetKycStatus } from '@repo/common/queries/kyc'
import {
  useGetIpData,
  useGetLocationAttestation,
  useSaveLocationAttestation,
} from '@repo/common/queries/location'

import { LegalRestrictionContainer } from '@/components/LegalRestrictionContainer'
import type { BasePanelProps } from '@/flows/flow_helpers'

import { VPNNotAllowed } from '../PanelLocationCheck'

export const PanelLocationAttestation = memo<BasePanelProps>(
  // eslint-disable-next-line max-lines-per-function, complexity
  function PanelLocationAttestation({ onCompleted }) {
    const { mutate, isPending, isReady } = useSaveLocationAttestation()
    const { data: location_attestation, isLoading } =
      useGetLocationAttestation()
    const ip_data_query = useGetIpData({ refetch_until_not_vpn: true })
    const posthog = usePostHog()
    const [joined_waiting_list, set_joined] = useState(false)
    const countries = useMemo(() => {
      const list = getData()
      return list.map((c) => ({ value: c.code, label: c.name }))
    }, [])

    const form = useForm({
      mode: 'controlled',
      initialValues: {
        attestation_country_code: ip_data_query.data?.country_code ?? '',
      },
      validate: {
        attestation_country_code: isNotEmpty('Country is required'),
      },
    })

    useEffect(() => {
      if (
        ip_data_query.data?.country_code &&
        form.getValues().attestation_country_code === ''
      ) {
        form.setValues({
          attestation_country_code: ip_data_query.data?.country_code,
        })
      }
      // only do when code first becomes available
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ip_data_query.data?.country_code])

    const onSubmit = (values: typeof form.values) => {
      mutate(values)
    }

    useEffect(() => {
      if (location_attestation?.passed) {
        onCompleted()
      }
    }, [location_attestation?.passed, onCompleted])

    const onJoin = useCallback(() => {
      set_joined(true)
      posthog.capture('waiting list', {
        type: 'us citizen',
      })
    }, [posthog])

    const did_not_pass =
      location_attestation?.passed == false &&
      location_attestation.status == 'completed'

    const loading = isLoading || ip_data_query.isLoading

    const disabled =
      loading || !isReady || location_attestation?.status == 'completed'

    let body
    if (loading) {
      body = <Loader color="primary" type="bars" />
    } else if (ip_data_query.data?.threat.is_anonymous) {
      body = <VPNNotAllowed />
    } else if (!did_not_pass) {
      body = (
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack justify="center">
            <Select
              label="What country are you a citizen?"
              w="100%"
              size="lg"
              placeholder="Pick value"
              disabled={disabled}
              data={countries}
              searchable
              {...form.getInputProps('attestation_country_code')}
            />
            <Button
              disabled={disabled}
              type="submit"
              size="lg"
              loading={isPending}
            >
              Submit
            </Button>
          </Stack>
        </form>
      )
    } else {
      body = (
        <Stack gap="xs" ta="center">
          <Text c="red" role="status">
            Location or Citizenship not allowed at the moment.
          </Text>
          <Text opacity={0.7}>
            If you have any questions or issues please reach out to us at{' '}
            <Anchor href="mailto:hello@fission.xyz">hello@fission.xyz</Anchor>.
          </Text>
          <Button onClick={onJoin} disabled={joined_waiting_list}>
            {joined_waiting_list ? 'Joined!' : 'Join waiting list'}
          </Button>
        </Stack>
      )
    }

    return <LegalRestrictionContainer>{body}</LegalRestrictionContainer>
  },
)

export const useSkipLocationVerify = () => {
  const location_query = useGetLocationAttestation()
  const kyc_query = useGetKycStatus()

  if (location_query.isLoading || kyc_query.isLoading) return false
  return Boolean(location_query.data?.passed || kyc_query.data?.passed)
}
